/* SPDX-License-Identifier: AGPL-3.0-or-later */

import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';
import { Globals } from '../global/globals';
import { UserLoginDto } from '@api';
import { WebsocketService } from './websocket.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly AUTH_BASE_URI: string = this.globals.backendUri + '/authentication';
  private readonly REGISTER_BASE_URI: string = this.globals.backendUri + '/user/register';

  constructor(
    private httpClient: HttpClient,
    private globals: Globals,
    private websocketService: WebsocketService,
  ) {}

  /**
   * Login in the user. If it was successful, a valid JWT token will be stored
   *
   * @param authRequest User data
   */
  loginUser(authRequest: UserLoginDto): Observable<string> {
    return this.httpClient
      .post(this.AUTH_BASE_URI, authRequest, { responseType: 'text' })
      .pipe(tap((authResponse: string) => this.setToken(authResponse)));
  }

  /**
   * Login in the user. If it was successful, a valid JWT token will be stored
   *
   * @param authRequest User data
   */
  registerUser(dto: UserLoginDto): Observable<string> {
    return this.httpClient
      .post(this.REGISTER_BASE_URI, dto, { responseType: 'text' })
      .pipe(tap((authResponse: string) => this.setToken(authResponse)));
  }

  /**
   * Check if a valid JWT token is saved in the localStorage
   */
  isLoggedIn() {
    const token = this.getToken();

    return !!token && this.getTokenExpirationDate(token).valueOf() > new Date().valueOf();
  }

  getUsername(): string | null {
    const token = this.getToken();

    if (!token) {
      return null;
    }

    const decoded = jwtDecode(token);
    return decoded.sub || null;
  }

  logoutUser() {
    console.log('Logout');
    localStorage.removeItem('authToken');
    this.websocketService.disconnect();
  }

  getToken() {
    return localStorage.getItem('authToken');
  }

  /**
   * Whether the given URL should be treated as unauthenticated or not.
   */
  isUnauthenticatedUrl(url: string): boolean {
    return url == this.AUTH_BASE_URI || url == this.REGISTER_BASE_URI;
  }

  private setToken(authResponse: string) {
    localStorage.setItem('authToken', authResponse);
  }

  private getTokenExpirationDate(token: string): Date {
    const decoded = jwtDecode(token);
    const date = new Date(0);

    if (decoded.exp === undefined) {
      return date;
    }

    date.setUTCSeconds(decoded.exp);
    return date;
  }
}
